/**
 * The own-domain entry ramp onto the free funnel.
 *
 * `/free-backlink-checker` asks for a competitor first. A large, distinctly
 * better-ranking slice of search demand asks the question the other way round
 * ("who links to my website", "check links to my site", "sites linking to my
 * site"), and those visitors bounce off a competitor-first hero because the
 * page appears to want a domain they have not thought about yet.
 *
 * So this page inverts the order: your site first, the competitor second. It is
 * the same `getOrCreateScan` call, the same email gate, and the same report
 * ladder underneath. Only the sequence and the framing change, which is why the
 * teaser math and report surfaces are imported rather than reimplemented.
 *
 * It deliberately does NOT duplicate `/guides/check-who-links-to-your-website`:
 * that page answers "how do I do this" at length, this one just does it. They
 * cross-link, and their FAQ sets are kept disjoint so the two FAQPage blocks
 * never compete for the same rich result.
 */
import { useEffect } from 'react'
import { data, redirect, Form, Link, useFetcher } from 'react-router'
import { ADS_CONVERSION_LABELS } from '#app/analytics/ads-conversions.ts'
import { getPosthogDistinctId } from '#app/analytics/attribution.server.ts'
import { trackClient } from '#app/analytics/client.ts'
import { fireAdsConversion } from '#app/analytics/google-ads.ts'
import { useTrackOnce } from '#app/analytics/use-track-once.ts'
import { FaqAccordion } from '#app/components/brand/faq.tsx'
import {
	Eyebrow,
	Heading,
	SectionKicker,
} from '#app/components/brand/section.tsx'
import { TurnstileWidget } from '#app/components/turnstile.tsx'
import { Button } from '#app/components/ui/button.js'
import { Icon, type IconName } from '#app/components/ui/icon.js'
import { requesterKeyFromIp, verifyTurnstile } from '#app/utils/abuse.server.ts'
import { prepareDomainForAPI } from '#app/utils/dataforseo-client.ts'
import {
	getClientIp,
	scanErrorResponse,
} from '#app/utils/scan-request.server.ts'
import {
	buildGap,
	buildTeaser,
	parentDomainHint,
	type Gap,
	type Teaser,
} from '#app/utils/scan-teaser.ts'
import { findFreshScan, getOrCreateScan } from '#app/utils/scan.server.ts'
import { buildMeta, faqPageJsonLd, webAppJsonLd } from '#app/utils/seo.ts'
import { type Route } from './+types/who-links-to-my-website.ts'
import { Footer } from './components/footer'
import Header from './components/header'
import { ReferringList, ReportHeader } from './components/scan-visuals'

export const meta: Route.MetaFunction = () => [
	...buildMeta({
		title: 'Who Links to My Website? Free Backlink Checker',
		description:
			'Paste your domain and see every site linking to you: how strong each one is, which pass authority, and what is new this month. Free, instant, no account.',
		path: '/who-links-to-my-website',
		image: '/img/og/who-links-to-my-website.png',
	}),
	{ 'script:ld+json': faqPageJsonLd(FAQS) },
	{
		'script:ld+json': webAppJsonLd({
			name: 'Who Links to My Website? Free Backlink Checker',
			description:
				'Paste your domain and see every site linking to you, with authority, follow status, and what arrived in the last 30 days. Free, no account.',
			path: '/who-links-to-my-website',
			image: '/img/og/who-links-to-my-website.png',
			breadcrumb: 'Who links to my website',
		}),
	},
]

// `opportunity` = domains linking to the rival but not to you (what to go earn).
// `edge` = domains linking to you but not to the rival (what you already hold).
// Both come from the same `buildGap`, run in opposite directions.
type Comparison = {
	rival: string
	rivalTotal: number
	opportunity: Gap
	edge: Gap
}

// --- Evergreen content (renders in every state, so a first-time visitor and a
// crawler both get a real answer to the question in the H1, not just a form) ---

// The direct answer, kept to the three sources that actually exist. Ordered by
// how fast they pay off, not by how impressive they sound.
const sources: { icon: IconName; title: string; text: string }[] = [
	{
		icon: 'magnifying-glass',
		title: 'A backlink checker',
		text: 'Paste your domain above and your referring domains come back in seconds, strongest first. This is the view the rest of the web already has of you, competitors included.',
	},
	{
		icon: 'logs',
		title: 'Google Search Console',
		text: 'Links, then External links, then Top linking sites. Google’s own record, free, and the only view that reflects what Google has actually counted. It covers verified properties only.',
	},
	{
		icon: 'chart-no-axes-column',
		title: 'Your referral traffic',
		text: 'Analytics shows which links people actually click. That is a far smaller set than the links that exist, and it tells you which of yours send real visitors rather than only authority.',
	},
]

// What the scan returns, framed around the visitor's own site.
const shows: { icon: IconName; title: string; text: string }[] = [
	{
		icon: 'logs',
		title: 'Every site linking to you',
		text: 'The full count of unique referring domains pointing at your site, with the 25 strongest shown free and the complete list in your emailed report.',
	},
	{
		icon: 'chart-no-axes-column',
		title: 'How strong each link is',
		text: 'A 0 to 100 Authority Rank per linking site, so the links worth protecting are obvious next to the directory entries nobody reads.',
	},
	{
		icon: 'link-2',
		title: 'Which links pass authority',
		text: 'Dofollow and nofollow labeled per domain. Nofollow links still bring traffic and credibility, they just do not pass ranking signal.',
	},
	{
		icon: 'plus-circled',
		title: 'What arrived this month',
		text: 'Referring domains first seen in the last 30 days are flagged, which is usually the fastest way to notice a mention you did not know you had.',
	},
]

// Three things worth doing with the list, in the order they pay off. This is
// the part most "who links to me" pages skip, and it is the reason the list is
// worth pulling in the first place.
const actions: { step: string; title: string; text: string }[] = [
	{
		step: '01',
		title: 'Protect what you already have',
		text: 'Note your highest-authority referring domains and the pages they point at. Redirect or delete one of those pages and you quietly hand back the ranking it was earning. Most lost link value is self-inflicted.',
	},
	{
		step: '02',
		title: 'Repeat what worked',
		text: 'Links cluster on the pages that deserve them. When several strong domains point at one guide or one tool, that is a repeatable format rather than a lucky break, and it is cheaper to make another one than to cold-pitch strangers.',
	},
	{
		step: '03',
		title: 'Question the rest, calmly',
		text: 'A handful of odd links is normal and Google ignores most of them. Look for a sudden pile from unrelated, low-authority sites, and only then consider a disavow. Panic-disavowing good links does far more damage than leaving bad ones alone.',
	},
]

const learnGuides = [
	{
		title: 'How to check who is linking to your website',
		href: '/guides/check-who-links-to-your-website',
	},
	{
		title: 'Check backlinks with Google',
		href: '/guides/check-backlinks-with-google',
	},
	{
		title: 'Dofollow vs nofollow backlinks',
		href: '/guides/dofollow-vs-nofollow-backlinks',
	},
	{
		title: 'Find and remove toxic backlinks',
		href: '/guides/find-and-remove-toxic-backlinks',
	},
]
const learnNext = [
	{
		title: 'How to find competitor backlinks',
		href: '/guides/find-competitor-backlinks',
	},
	{ title: 'Backlink gap analysis', href: '/guides/backlink-gap-analysis' },
	{
		title: 'Free competitor backlink checker',
		href: '/free-backlink-checker',
	},
	{ title: 'How to get backlinks', href: '/guides/how-to-get-backlinks' },
	{
		title: 'Should you buy backlinks?',
		href: '/guides/should-you-buy-backlinks',
	},
]

// One source of truth for the FAQ: rendered on the page AND emitted as FAQPage
// JSON-LD in the meta above. Keep the strings identical (no drift). These are
// kept deliberately disjoint from the checker's and the guide's FAQ sets.
const FAQS: ReadonlyArray<{ q: string; a: string }> = [
	{
		q: 'How do I find out who links to my website?',
		a: 'Paste your domain into a backlink checker and it returns your referring domains, which are the unique sites linking to you. Google Search Console lists your top linking sites for free under Links, External links, Top linking sites. Use both: the checker shows what the whole web can see, Search Console shows what Google has actually counted.',
	},
	{
		q: 'Is this free, and do I need an account?',
		a: 'It is free and there is no account or card. Enter your domain and you immediately see your total referring domains, the 25 strongest by authority, the dofollow split, and anything new in the last 30 days. You only enter an email if you want the complete list and a CSV sent to you.',
	},
	{
		q: 'Why does Search Console show different links than a backlink checker?',
		a: 'They are measuring different things. Search Console reports what Google has crawled and attributed to your verified property, which lags and omits links it has not processed. A checker reports what its own crawler has indexed across the web. When a checker sees a link Search Console does not, it usually has not been counted yet. When Search Console lists one a checker misses, it is often behind a redirect or on a page crawlers rarely reach.',
	},
	{
		q: 'What should I do with the list once I have it?',
		a: 'Three things, in order. Protect your strongest referring domains by never breaking the pages they point at. Look at which of your pages attract links and make more of that kind. Then review anything spammy, without rushing to disavow, since Google already ignores most low-quality links on its own.',
	},
	{
		q: 'Can links to my site disappear without me noticing?',
		a: 'Yes, and it is common. Sites get redesigned, articles get pruned, and roundups get rewritten, all of which can drop your link with no notification. That is why the check is worth repeating monthly rather than once. A link lost from a high-authority domain is worth an email asking for it back, and those requests succeed more often than cold outreach does.',
	},
	{
		q: 'Can I see who links to my competitors as well?',
		a: 'Yes, and it is the more useful half. After scanning your own site, add a competitor domain and you get both directions: the sites linking to them but not to you, which are your most realistic next links because they already link to a business like yours, and the sites linking to you but not to them, which is the advantage you already hold.',
	},
]

export async function loader({ request }: Route.LoaderArgs) {
	const url = new URL(request.url)
	const rawSite = url.searchParams.get('site')?.trim() || null
	const rawRival = url.searchParams.get('vs')?.trim() || null
	const siteKey = process.env.TURNSTILE_SITE_KEY ?? null

	// The visitor's own profile (served from cache if already scanned; no cost).
	let teaser: Teaser | null = null
	let siteScan: Awaited<ReturnType<typeof findFreshScan>> = null
	if (rawSite) {
		const siteDomain = prepareDomainForAPI(rawSite).toLowerCase()
		if (siteDomain) {
			siteScan = await findFreshScan(siteDomain)
			if (siteScan) teaser = buildTeaser(siteScan)
		}
	}

	// The comparison, computed on demand from the two cached scans.
	let comparison: Comparison | null = null
	if (siteScan && rawRival) {
		const rivalDomain = prepareDomainForAPI(rawRival).toLowerCase()
		if (rivalDomain && rivalDomain !== siteScan.targetDomain) {
			const rivalScan = await findFreshScan(rivalDomain)
			if (rivalScan) {
				comparison = {
					rival: rivalScan.targetDomain,
					rivalTotal: rivalScan.referringDomainsFound,
					opportunity: buildGap(rivalScan, siteScan, siteScan.targetDomain),
					edge: buildGap(siteScan, rivalScan, rivalScan.targetDomain),
				}
			}
		}
	}

	return { site: rawSite, rival: rawRival, siteKey, teaser, comparison }
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const intent = String(formData.get('intent') ?? 'site')
	const site = String(formData.get('site') ?? '').trim()
	if (!site) {
		return data(
			{ error: 'Enter your domain to see who links to you.', step: 'site' },
			{ status: 400 },
		)
	}

	// Bot check before any paid work.
	const ip = getClientIp(request)
	const token = formData.get('cf-turnstile-response')
	const passed = await verifyTurnstile(
		typeof token === 'string' ? token : null,
		ip ?? undefined,
	)
	if (!passed) {
		return data(
			{
				error: 'That bot check did not pass. Please try again.',
				step: intent === 'compare' ? 'compare' : 'site',
			},
			{ status: 400 },
		)
	}

	// Rate limit + global spend ceiling are enforced atomically inside
	// getOrCreateScan (only on a cache MISS), so cached domains are never
	// rejected. We just need the requester key here.
	const requesterKey = requesterKeyFromIp(ip ?? 'unknown')

	// A failed scan is a funnel drop only the server can explain (rate limit vs
	// spend ceiling vs provider), so the reason is attached server-side.
	const distinctId = getPosthogDistinctId(request)

	// Step 2: add a competitor and compare both ways.
	if (intent === 'compare') {
		const rival = String(formData.get('vs') ?? '').trim()
		if (!rival) {
			return data(
				{
					error: 'Enter a competitor domain to compare against.',
					step: 'compare',
				},
				{ status: 400 },
			)
		}
		// Caught here rather than in the loader: comparing a site with itself
		// yields an empty gap that reads as "you are missing nothing", which is
		// worse than saying so. Costs nothing to reject before the scan.
		if (
			prepareDomainForAPI(rival).toLowerCase() ===
			prepareDomainForAPI(site).toLowerCase()
		) {
			return data(
				{
					error: 'That is your own domain. Enter a competitor to compare.',
					step: 'compare',
				},
				{ status: 400 },
			)
		}
		try {
			const rivalScan = await getOrCreateScan(rival, {
				source: 'who-links-compare',
				requesterKey,
			})
			return redirect(
				`/who-links-to-my-website?site=${encodeURIComponent(
					site,
				)}&vs=${encodeURIComponent(rivalScan.targetDomain)}`,
			)
		} catch (err) {
			return scanErrorResponse(
				err,
				'We could not analyze that competitor. Double-check the domain and try again.',
				{ distinctId, step: 'compare' },
			)
		}
	}

	// Default: scan the visitor's own site.
	try {
		const scan = await getOrCreateScan(site, {
			source: 'who-links-to-me',
			requesterKey,
		})
		// Post/Redirect/Get: land on a shareable, refresh-safe URL that renders
		// the (now cached) teaser via the loader.
		return redirect(
			`/who-links-to-my-website?site=${encodeURIComponent(scan.targetDomain)}`,
		)
	} catch (err) {
		return scanErrorResponse(
			err,
			'We could not analyze that domain. Double-check it and try again.',
			{ distinctId, step: 'site' },
		)
	}
}

export default function WhoLinksToMyWebsite({
	loaderData,
	actionData,
}: Route.ComponentProps) {
	const { site, siteKey, teaser, comparison } = loaderData
	// Two forms on this page, so an error belongs under the one that failed.
	const error = actionData?.error
	const failedStep = actionData?.step
	const siteError = failedStep === 'site' ? (error ?? null) : null
	const compareError = failedStep === 'compare' ? (error ?? null) : null

	// Same funnel events as the competitor-first ramp, tagged with the entry
	// point. One funnel, two doors: separate event names would split the report
	// and make neither door's conversion rate readable.
	useTrackOnce('scan_viewed', teaser?.target, {
		entry: 'own-domain',
		total_referring_domains: teaser?.totalReferringDomains,
		high_authority: teaser?.highAuthority,
		new_last_30d: teaser?.newLast30d,
	})

	// "Nobody links to you" is a true and motivating answer on THIS ramp, not a
	// dead end, so nothing here is hidden the way it is on the competitor-first
	// page. Only the email gate changes: alone, the report would be the visitor's
	// own empty list, and there is nothing to send. With a rival the report is the
	// RIVAL's list and every row is an opportunity, which is the strongest thing
	// this page ever offers. So the ask becomes "add a competitor" rather than
	// "give us your email", which is the same sales moment one step earlier.
	const awaitingRival =
		teaser !== null && teaser.totalReferringDomains === 0 && !comparison

	return (
		<div className="bg-canvas text-ink min-h-screen font-sans">
			<Header />
			<main className="mx-auto max-w-[1080px] px-6 py-14 sm:px-8">
				<div className="mx-auto max-w-[680px] text-center">
					<Eyebrow className="mb-4">Free tool</Eyebrow>
					<Heading
						as="h1"
						className="text-[clamp(32px,5vw,48px)] leading-[1.05] tracking-[-0.03em]"
					>
						Who links to my website?
					</Heading>
					<p className="mt-4 text-xl leading-relaxed text-pretty text-[#57565E]">
						Enter your domain and we pull the sites linking to you from a live
						backlink index: every referring domain, how strong each one is, and
						what arrived in the last 30 days. Free, no account.
					</p>

					<Form
						method="post"
						onSubmit={(event) =>
							trackClient('scan_submitted', {
								entry: 'own-domain',
								has_prior_scan: Boolean(teaser),
								domain:
									String(
										new FormData(event.currentTarget).get('site') ?? '',
									).trim() || undefined,
							})
						}
						className="border-hairline mx-auto mt-7 flex max-w-[500px] flex-col gap-2.5 rounded-2xl border bg-white p-2 shadow-[0_1px_2px_rgba(21,21,25,.04),0_14px_34px_-20px_rgba(21,21,25,.2)]"
					>
						<div className="flex gap-2.5">
							<div className="flex flex-1 items-center gap-2.5 pl-3.5">
								<Icon
									name="magnifying-glass"
									className="size-[18px] text-[#B5B1A8]"
								/>
								<label htmlFor="own-site" className="sr-only">
									Your website domain
								</label>
								<input
									id="own-site"
									name="site"
									defaultValue={site ?? ''}
									placeholder="your-domain.com"
									spellCheck={false}
									autoComplete="off"
									className="text-ink w-full flex-1 border-none bg-transparent py-3 font-mono text-base outline-none"
								/>
							</div>
							<Button
								type="submit"
								size="lg"
								className="h-auto rounded-[11px] px-6 py-3 text-[15.5px]"
							>
								Show my links
							</Button>
						</div>
						{siteKey ? <TurnstileWidget siteKey={siteKey} /> : null}
					</Form>

					{siteError ? (
						<p className="text-lost mt-3 text-sm font-medium" role="alert">
							{siteError}
						</p>
					) : null}
				</div>

				{teaser ? (
					<>
						<ReportHeader
							teaser={teaser}
							kicker="Your inbound links"
							countLabel={['sites link', 'to you']}
							ariaLabel="Your backlink overview"
						/>
						<CompareSection
							teaser={teaser}
							comparison={comparison}
							siteKey={siteKey}
							error={compareError}
						/>
						{awaitingRival ? (
							<NoLinksYet domain={teaser.target} />
						) : (
							<ConversionCta
								teaser={teaser}
								comparison={comparison}
								siteKey={siteKey}
							/>
						)}
						{/* With no rival this would follow "No sites link to X yet" with a
						    second empty panel saying the same thing. The empty list earns
						    its place only alongside a comparison, where their count stands
						    next to your 0. */}
						{awaitingRival ? null : (
							<ReferringList
								teaser={teaser}
								heading="Sites linking to you"
								ariaLabel="Sites linking to you"
							/>
						)}
						<WhatToDo />
					</>
				) : (
					<>
						<EmptyState hasSite={Boolean(site)} />
						<ShortAnswer />
						<WhatItShows />
						<WhatToDo />
						<CompetitorPivot />
					</>
				)}

				<Faq />
				<LearnMore />
			</main>
			<Footer />
		</div>
	)
}

// Step 2, and the reason this page earns its keep commercially: once someone
// has seen their own list, "what does a competitor have that I don't" is the
// obvious next question, and it is the question the paid report answers.
function CompareSection({
	teaser,
	comparison,
	siteKey,
	error,
}: {
	teaser: Teaser
	comparison: Comparison | null
	siteKey: string | null
	error: string | null
}) {
	useTrackOnce(
		'gap_viewed',
		comparison ? `${teaser.target}|${comparison.rival}` : null,
		{
			entry: 'own-domain',
			gap_count: comparison?.opportunity.count,
			edge_count: comparison?.edge.count,
			total_referring_domains: teaser.totalReferringDomains,
		},
	)

	if (comparison) {
		const { opportunity, edge, rival } = comparison
		const remaining = Math.max(opportunity.count - opportunity.top.length, 0)
		return (
			<section className="mt-14 text-center" aria-label="Your backlink gap">
				<div className="text-brand font-mono text-[11px] tracking-[0.18em] uppercase">
					You vs {rival}
				</div>
				<p className="font-display text-ink mx-auto mt-3 max-w-[760px] text-[clamp(22px,3.2vw,34px)] leading-[1.15] font-semibold tracking-[-0.01em]">
					<span className="text-brand">
						{opportunity.count.toLocaleString()}
					</span>{' '}
					sites link to {rival} but not to{' '}
					<span className="whitespace-nowrap">{teaser.target}</span> yet.
				</p>
				<p className="mx-auto mt-2 max-w-[560px] text-[15px] text-[#57565E]">
					Every one of them already links to a business like yours, which makes
					them the most realistic links you could earn next.
				</p>
				{opportunity.top.length ? (
					<div className="mx-auto mt-6 flex max-w-[760px] flex-wrap justify-center gap-2">
						{opportunity.top.map((row) => (
							<span
								key={row.domain}
								className="border-hairline text-ink inline-flex items-center gap-2 rounded-full border bg-white px-3.5 py-1.5 font-mono text-[12.5px]"
							>
								{row.domain}
								<span className="text-[#8A867D]">AR {row.authority}</span>
							</span>
						))}
						{remaining > 0 ? (
							<span className="bg-brand-wash text-brand inline-flex items-center rounded-full px-3.5 py-1.5 font-mono text-[12.5px] font-medium">
								+{remaining.toLocaleString()} more
							</span>
						) : null}
					</div>
				) : null}
				<p className="mx-auto mt-6 max-w-[600px] text-[14px] text-[#8A867D]">
					{rival} has{' '}
					<strong className="text-ink font-semibold">
						{comparison.rivalTotal.toLocaleString()}
					</strong>{' '}
					referring domains to your{' '}
					<strong className="text-ink font-semibold">
						{teaser.totalReferringDomains.toLocaleString()}
					</strong>
					.
					{edge.count > 0 ? (
						<>
							{' '}
							The comparison runs both ways though:{' '}
							<strong className="text-ink font-semibold">
								{edge.count.toLocaleString()}
							</strong>{' '}
							sites link to you but not to them. That is the advantage you
							already hold, and the part of your profile most worth protecting.
						</>
					) : null}
				</p>
			</section>
		)
	}

	return (
		<section
			id="add-competitor"
			className="mt-14 scroll-mt-8 text-center"
			aria-label="Compare with a competitor"
		>
			<h2 className="font-display text-ink text-[clamp(20px,2.6vw,28px)] font-semibold tracking-[-0.01em]">
				Now see what a competitor has that you do not
			</h2>
			<p className="mx-auto mt-2 max-w-[560px] text-[15px] text-[#57565E]">
				Add any site you compete with in search. We will show the domains
				linking to them but not to {teaser.target}, and the ones linking to you
				but not to them.
			</p>
			<Form
				method="post"
				preventScrollReset
				onSubmit={() =>
					trackClient('gap_submitted', {
						entry: 'own-domain',
						domain: teaser.target,
					})
				}
				className="mx-auto mt-5 flex max-w-[440px] flex-col gap-3"
			>
				<input type="hidden" name="intent" value="compare" />
				<input type="hidden" name="site" value={teaser.target} />
				<div className="border-hairline focus-within:border-brand flex items-center gap-2 rounded-full border bg-white p-1.5 pl-4 transition-colors">
					<label htmlFor="rival-domain" className="sr-only">
						Competitor domain
					</label>
					<input
						id="rival-domain"
						name="vs"
						placeholder="competitor-domain.com"
						spellCheck={false}
						autoComplete="off"
						required
						className="text-ink min-w-0 flex-1 border-none bg-transparent font-mono text-sm outline-none"
					/>
					<Button
						type="submit"
						className="h-9 rounded-full px-5 whitespace-nowrap"
					>
						Compare us
					</Button>
				</div>
				{siteKey ? <TurnstileWidget siteKey={siteKey} /> : null}
				{error ? (
					<p className="text-lost text-sm font-medium" role="alert">
						{error}
					</p>
				) : null}
			</Form>
		</section>
	)
}

// Replaces the email gate for a visitor with no links and no rival yet. Same
// violet band, because this is still the page's one conversion moment: the ask
// is just a competitor rather than an email, since a competitor is what turns
// their empty profile into a report worth sending.
function NoLinksYet({ domain }: { domain: string }) {
	const parent = parentDomainHint(domain)
	return (
		<section className="mt-14" aria-label="Add a competitor">
			<div className="bg-brand relative overflow-hidden rounded-[28px] px-7 py-9 text-center text-white sm:px-10">
				<div className="pointer-events-none absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
				<div className="relative">
					<h2 className="font-display text-[clamp(22px,3vw,30px)] font-semibold tracking-[-0.01em]">
						No sites link to {domain} yet
					</h2>
					<p className="mx-auto mt-2 max-w-[560px] text-[14.5px] text-white/80">
						That is where most sites start, and it is fixable. Add a competitor
						and we will show you exactly which sites link to them, because every
						one of those is a link that is open to you right now.
					</p>
					<a
						href="#add-competitor"
						className="text-brand mt-6 inline-flex h-11 items-center rounded-full bg-white px-6 font-medium hover:bg-white/90"
					>
						Add a competitor
					</a>
					{parent ? (
						<p className="mx-auto mt-4 max-w-[480px] text-[12.5px] text-white/70">
							Scanned a subdomain? If you meant {parent}, enter that at the top
							and we will map it instead.
						</p>
					) : null}
				</div>
			</div>
		</section>
	)
}

// The email gate. What the report actually contains differs by state, so the
// copy branches with it rather than promising the same thing in both.
function ConversionCta({
	teaser,
	comparison,
	siteKey,
}: {
	teaser: Teaser
	comparison: Comparison | null
	siteKey: string | null
}) {
	const fetcher = useFetcher<{
		ok?: boolean
		error?: string
		reportId?: string
	}>()
	const sent = fetcher.data?.ok === true
	const error = fetcher.data?.error
	const pending = fetcher.state !== 'idle'
	const reportId = fetcher.data?.reportId

	// The report is always built around the domain whose FULL list is delivered.
	// Alone, that is your own site. After a comparison it is the competitor,
	// tagged with which of their links you already share.
	const reportDomain = comparison ? comparison.rival : teaser.target
	const reportOwner = comparison ? teaser.target : null

	// Same trigger as the competitor-first ramp: the SUCCESSFUL response, keyed
	// on the report id. This ramp makes the hazard especially easy to hit —
	// adding a competitor flips `reportDomain` from your own site to the rival
	// while this component stays mounted, so a domain-keyed effect would fire a
	// second lead off the first submission's stale success.
	useEffect(() => {
		if (!reportId) return
		fireAdsConversion({
			label: ADS_CONVERSION_LABELS.lead,
			dedupeKey: `lead_${reportId}`,
		})
	}, [reportId])

	return (
		<section className="mt-14" aria-label="Get the full report">
			<div className="bg-brand relative overflow-hidden rounded-[28px] px-7 py-9 text-center text-white sm:px-10">
				<div className="pointer-events-none absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
				<div className="relative">
					{sent ? (
						<>
							<h2 className="font-display text-[clamp(22px,3vw,30px)] font-semibold tracking-[-0.01em]">
								Check your inbox
							</h2>
							<p className="mx-auto mt-2 max-w-[520px] text-[14.5px] text-white/80">
								Your full report is on its way, with the complete list and a CSV
								you can sort however you like.
							</p>
							<p className="mt-4 text-[13px] text-white/70">
								Want to save your reports and come back to them?{' '}
								<Link
									to="/signup"
									className="font-medium text-white underline underline-offset-2"
								>
									Create a free account
								</Link>
								.
							</p>
						</>
					) : (
						<>
							<h2 className="font-display text-[clamp(22px,3vw,30px)] font-semibold tracking-[-0.01em]">
								Get the complete list, free
							</h2>
							<p className="mx-auto mt-2 max-w-[580px] text-[14.5px] text-white/80">
								{comparison
									? `We will email every referring domain for ${comparison.rival}, each one flagged for whether you already have it, so the ${comparison.opportunity.count.toLocaleString()} you are missing are in one place. Plus a CSV.`
									: `You are seeing the ${teaser.rows.length} strongest of ${teaser.totalReferringDomains.toLocaleString()} sites linking to you. Enter your email for the complete list and a CSV.`}
							</p>

							{!comparison ? (
								<p className="mx-auto mt-2 max-w-[480px] text-[12.5px] text-white/70">
									Tip: add a competitor in the section above first and your gap
									against them is included in the same report.
								</p>
							) : null}

							<fetcher.Form
								method="post"
								action="/resources/scan-report"
								onSubmit={(event) =>
									trackClient('report_requested', {
										entry: 'own-domain',
										domain: reportDomain,
										has_gap: Boolean(comparison),
										gap_count: comparison?.opportunity.count,
										marketing_consent:
											new FormData(event.currentTarget).get('consent') === 'on',
									})
								}
								className="mx-auto mt-5 flex max-w-[460px] flex-col gap-3"
							>
								<input type="hidden" name="domain" value={reportDomain} />
								{reportOwner ? (
									<input type="hidden" name="owner" value={reportOwner} />
								) : null}
								<input type="hidden" name="source" value="who-links-to-me" />
								<div className="flex flex-col gap-2 sm:flex-row">
									<label htmlFor="report-email" className="sr-only">
										Email address for your report
									</label>
									<input
										id="report-email"
										name="email"
										type="email"
										required
										placeholder="you@company.com"
										className="h-11 flex-1 rounded-full border border-white/20 bg-white/10 px-4 font-mono text-sm text-white outline-none placeholder:text-white/50 focus:border-white/50"
									/>
									<Button
										type="submit"
										disabled={pending}
										className="text-brand h-11 rounded-full bg-white px-6 font-medium whitespace-nowrap hover:bg-white/90 disabled:opacity-70"
									>
										{pending ? 'Sending...' : 'Email my report'}
									</Button>
								</div>
								<label className="mx-auto flex max-w-[420px] items-start gap-2 text-left text-[12.5px] text-white/70">
									<input
										type="checkbox"
										name="consent"
										className="mt-0.5 accent-white"
									/>
									<span>
										Also send me occasional tips on winning backlinks. No spam,
										unsubscribe anytime.
									</span>
								</label>
								{siteKey ? <TurnstileWidget siteKey={siteKey} /> : null}
								{error ? (
									<p
										className="text-[13px] font-medium text-white"
										role="alert"
									>
										{error}
									</p>
								) : null}
							</fetcher.Form>

							<p className="mt-4 text-[13px] text-white/70">
								Ready to go further?{' '}
								<Link
									to="/signup"
									className="font-medium text-white underline underline-offset-2"
								>
									Create a free account
								</Link>{' '}
								or{' '}
								<Link
									to="/pricing"
									className="font-medium text-white underline underline-offset-2"
								>
									see pricing
								</Link>
								.
							</p>
						</>
					)}
				</div>
			</div>
		</section>
	)
}

function EmptyState({ hasSite }: { hasSite: boolean }) {
	return (
		<div className="mx-auto mt-14 max-w-[520px] text-center">
			<Icon name="link-2" className="text-brand/40 mx-auto mb-4 size-8" />
			<p className="font-display text-ink text-lg font-semibold">
				{hasSite
					? 'Hit Show my links to run the check.'
					: 'Enter your domain to see who is linking to you.'}
			</p>
			<p className="mt-1.5 text-[14px] text-[#8A867D]">
				Free and instant. We pull your referring domains live.
			</p>
		</div>
	)
}

// The direct answer to the H1, above everything else on the cold page. Three
// real sources, what each one is actually good for, and the reason they
// disagree, which is the part that trips people up.
function ShortAnswer() {
	return (
		<section className="mt-16" aria-label="How to find out who links to you">
			<div className="border-hairline relative overflow-hidden rounded-[28px] border bg-white p-6 sm:p-10">
				<div className="from-brand-wash/70 pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b to-transparent" />
				<div className="relative">
					<div className="max-w-[620px]">
						<SectionKicker>The short answer</SectionKicker>
						<h2 className="font-display text-ink mt-4 text-[clamp(24px,3.4vw,34px)] leading-[1.08] font-semibold tracking-[-0.02em]">
							Three places show you who links to your site
						</h2>
						<p className="mt-3 text-[15px] leading-relaxed text-[#57565E]">
							No single source sees every link. Two of these take under a minute
							and together they cover almost everything worth knowing.
						</p>
					</div>

					<ol className="border-hairline bg-hairline mt-9 grid gap-px overflow-hidden rounded-2xl border sm:grid-cols-3">
						{sources.map((source, i) => (
							<li
								key={source.title}
								className="group hover:bg-brand-wash/30 relative bg-white p-6 transition-colors"
							>
								<div className="flex items-center justify-between">
									<span className="font-display text-brand/15 text-[44px] leading-none font-semibold tabular-nums">
										0{i + 1}
									</span>
									<span className="bg-brand/[0.1] text-brand flex size-9 items-center justify-center rounded-xl transition-transform group-hover:-translate-y-0.5">
										<Icon name={source.icon} className="size-[18px]" />
									</span>
								</div>
								<h3 className="font-display text-ink mt-6 text-[16px] font-semibold">
									{source.title}
								</h3>
								<p className="mt-1.5 text-[14px] leading-relaxed text-[#57565E]">
									{source.text}
								</p>
							</li>
						))}
					</ol>

					<p className="mt-7 max-w-[720px] text-[15px] leading-relaxed text-[#57565E]">
						When a checker and Search Console disagree, the disagreement is
						itself useful. A link a checker sees but Search Console does not has
						usually not been counted yet. A link Search Console lists that a
						checker misses is often behind a redirect or sitting on a page
						crawlers rarely reach. For the full walkthrough of both, read{' '}
						<Link
							to="/guides/check-who-links-to-your-website"
							className="text-brand font-medium underline underline-offset-2"
						>
							how to check who is linking to your website
						</Link>
						.
					</p>
				</div>
			</div>
		</section>
	)
}

function WhatItShows() {
	return (
		<section
			className="mt-24 grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-14"
			aria-label="What the check shows"
		>
			<div className="lg:pt-3">
				<SectionKicker>What you get</SectionKicker>
				<h2 className="font-display text-ink mt-4 text-[clamp(24px,3.4vw,34px)] leading-[1.06] font-semibold tracking-[-0.02em]">
					Your whole inbound link profile, in plain language
				</h2>
				<p className="mt-4 max-w-[420px] text-[15px] leading-relaxed text-[#57565E]">
					Every metric is defined on the page and none of it needs a manual.
					Here is what comes back the moment you run it, free.
				</p>
			</div>

			<div className="grid gap-4 sm:grid-cols-2">
				{shows.map((item) => (
					<div
						key={item.title}
						className="border-hairline group rounded-2xl border bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_1px_2px_rgba(21,21,25,.04),0_18px_40px_-24px_rgba(21,21,25,.28)]"
					>
						<span className="bg-brand/[0.1] text-brand flex size-10 items-center justify-center rounded-xl">
							<Icon name={item.icon} className="size-5" />
						</span>
						<h3 className="font-display text-ink mt-4 text-[15.5px] font-semibold">
							{item.title}
						</h3>
						<p className="mt-1.5 text-[13.5px] leading-relaxed text-[#57565E]">
							{item.text}
						</p>
					</div>
				))}
			</div>
		</section>
	)
}

// Open, numbered editorial rows. Deliberately a different container language
// from the bordered panel and the card grid above it.
function WhatToDo() {
	return (
		<section className="mt-24" aria-label="What to do with the list">
			<div className="max-w-[620px]">
				<SectionKicker>Then what</SectionKicker>
				<h2 className="font-display text-ink mt-4 text-[clamp(24px,3.4vw,34px)] leading-[1.06] font-semibold tracking-[-0.02em]">
					A list of links is not the point. This is.
				</h2>
				<p className="mt-4 text-[15px] leading-relaxed text-[#57565E]">
					Most people pull the list, feel briefly informed, and close the tab.
					Three things are worth doing with it, in this order.
				</p>
			</div>

			<div className="mt-10 grid gap-px">
				{actions.map((action) => (
					<div
						key={action.step}
						className="border-hairline grid gap-3 border-t py-7 last:border-b sm:grid-cols-[auto_1fr] sm:gap-10"
					>
						<span className="font-display text-brand/25 text-[40px] leading-none font-semibold tabular-nums sm:w-[80px]">
							{action.step}
						</span>
						<div className="max-w-[680px]">
							<h3 className="font-display text-ink text-[18px] font-semibold">
								{action.title}
							</h3>
							<p className="mt-2 text-[15px] leading-relaxed text-[#57565E]">
								{action.text}
							</p>
						</div>
					</div>
				))}
			</div>
		</section>
	)
}

// The commercial bridge on the cold page: the reason to care about a
// competitor's links once you have seen your own.
function CompetitorPivot() {
	return (
		<section className="mt-24" aria-label="Compare against a competitor">
			<div className="bg-ink relative overflow-hidden rounded-[28px] px-7 py-12 text-white sm:px-12 sm:py-16">
				<div className="bg-brand/25 pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full blur-3xl" />
				<div className="bg-gained/10 pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full blur-3xl" />
				<div className="relative grid items-center gap-10 lg:grid-cols-[auto_1fr] lg:gap-16">
					<div className="text-center lg:text-left">
						<div className="font-display text-brand-light text-[clamp(76px,13vw,140px)] leading-[0.85] font-semibold tracking-[-0.04em]">
							~90%
						</div>
						<div className="mt-3 font-mono text-[11px] tracking-[0.16em] text-white/45 uppercase">
							of a rival&rsquo;s links are reproducible
						</div>
					</div>
					<div>
						<h2 className="font-display text-[clamp(22px,3vw,32px)] leading-[1.14] font-semibold tracking-[-0.02em] text-white">
							Knowing who links to you is half the picture. The other half is
							who links to them.
						</h2>
						<p className="mt-4 max-w-[540px] text-[15px] leading-relaxed text-white/65">
							Your own list tells you what to protect. A competitor&rsquo;s list
							tells you what to go get. Most of their links are reproducible
							tactics rather than luck: a guest post, a resource page, a tool
							roundup, a podcast. Any site already linking to a competitor has
							proven it will link to a business like yours, which makes it a far
							warmer target than a stranger. Run your own domain above, then add
							a competitor to see both sides at once.
						</p>
						<Link
							to="/free-backlink-checker"
							className="text-brand-light mt-6 inline-flex items-center gap-2 text-[15px] font-medium underline underline-offset-4"
						>
							Or start from a competitor instead
							<Icon name="arrow-right" className="size-4" />
						</Link>
					</div>
				</div>
			</div>
		</section>
	)
}

function Faq() {
	return (
		<section
			className="mt-24 grid gap-10 lg:grid-cols-[0.62fr_1.38fr] lg:gap-14"
			aria-label="Frequently asked questions"
		>
			<div>
				<SectionKicker>Questions</SectionKicker>
				<h2 className="font-display text-ink mt-4 text-[clamp(24px,3.4vw,34px)] leading-[1.06] font-semibold tracking-[-0.02em]">
					About your inbound links
				</h2>
				<p className="mt-4 max-w-[320px] text-[15px] leading-relaxed text-[#57565E]">
					Want the long version? Read the full{' '}
					<Link
						to="/guides/check-who-links-to-your-website"
						className="text-brand font-medium underline underline-offset-2"
					>
						guide to checking who links to your website
					</Link>
					.
				</p>
			</div>

			<FaqAccordion faqs={FAQS} />
		</section>
	)
}

function LearnMore() {
	const columns = [
		{ heading: 'Your own links', links: learnGuides },
		{ heading: 'Go on the offensive', links: learnNext },
	]
	return (
		<section
			className="border-hairline mt-24 border-t pt-12"
			aria-label="Keep learning"
		>
			<div className="mb-8 flex flex-wrap items-baseline gap-x-4 gap-y-1">
				<h2 className="font-display text-ink text-[clamp(20px,2.6vw,26px)] font-semibold tracking-[-0.01em]">
					Keep going
				</h2>
				<span className="text-[14px] text-[#8A867D]">
					Protect the links you have, then go earn the ones you do not.
				</span>
			</div>
			<div className="grid gap-x-14 gap-y-9 sm:grid-cols-2">
				{columns.map((col) => (
					<div key={col.heading}>
						<div className="text-brand mb-1 font-mono text-[11px] tracking-[0.18em] uppercase">
							{col.heading}
						</div>
						<ul>
							{col.links.map((l) => (
								<li key={l.href}>
									<Link
										to={l.href}
										className="group border-hairline flex items-center justify-between gap-4 border-b py-3.5 last:border-b-0"
									>
										<span className="text-ink group-hover:text-brand text-[15.5px] font-medium transition-colors">
											{l.title}
										</span>
										<Icon
											name="arrow-right"
											className="group-hover:text-brand size-4 text-[#B5B1A8] transition-transform group-hover:translate-x-1"
										/>
									</Link>
								</li>
							))}
						</ul>
					</div>
				))}
			</div>
		</section>
	)
}
